import { useEffect, useState } from "react";
import "./ForceModule.css";
import arrow from "./Assets/arrow.png";
import copyBtn from "./Assets/copyBtn.png";

const ForceModule = ({ config }) => {
    //unpack some of the config
    const maths = config.maths;
    const rtps = Object.keys(maths);

    //states
    const [rtp, setRtp] = useState(rtps[0]);
    const [dataRtp, setDataRtp] = useState(rtps[0]);
    const [reelSets, setReelSets] = useState({});
    const [selectedReelSet, setSelectedReelSet] = useState("");
    const [reelStops, setReelStops] = useState(new Array(config.reels).fill(0));
    const [rejectionSet, setRejectionSet] = useState(false);
    const [displayData, setDisplayData] = useState([]);
    const [showIds, setShowIds] = useState(false);
    const [output, setOutput] = useState("");
    const [copiedStyle, setCopiedStyle] = useState({
        margin: 0,
        opacity: 0,
    });

    // RTP Updated
    useEffect(() => {
        //Check to see if this is a reduction / rejection model
        if (maths[rtp].reductionWeight1M) {
            //cycle through RTPs and find version with reel sets
            for (const [key, value] of Object.entries(maths)) {
                if (value.reelSets) {
                    setRejectionSet(true);
                    setReelSets(value.reelSets);
                    setDataRtp(key);
                    break;
                }
            }
        } else {
            setRejectionSet(false);
            setDataRtp(rtp);
            setReelSets(maths[rtp].reelSets);
        }

        setSelectedReelSet(0);
    }, [rtp]);

    // New Reel Set Selected
    useEffect(() => {
        setReelStops(new Array(config.reels).fill(0));
    }, [selectedReelSet]);

    // Reel Stops Updated
    useEffect(() => {
        // Figure out and set the display Data
        const reelStrips = reelSets[selectedReelSet];

        if (!reelStrips) return;

        const display = reelStops.map((stopIndex, reelIndex) => {
            const reelStrip = reelStrips[reelIndex];

            //calculate indexes with overflow
            const indexArray = [];
            for (let i = -1; i <= config.symbolPerReel; i++) {
                let arrayIndex = (stopIndex + i) % reelStrip.length;

                if (arrayIndex < 0) {
                    arrayIndex = reelStrip.length - 1;
                }
                indexArray.push(arrayIndex);
            }

            return indexArray.map((index) => reelStrip[index]);
        });

        setDisplayData(display);
    }, [reelStops]);

    // Updates the output
    useEffect(() => {
        if (!maths[dataRtp].weights) return;

        const isFs = selectedReelSet[0] == "f";
        const weights = maths[dataRtp].weights[isFs ? "freeSpins" : "baseGame"];

        let weightPickIndex = selectedReelSet;
        let fsWeightLevel;
        /// No loss rejection on free spins
        setRejectionSet(!isFs);
        if (isFs) {
            const split = selectedReelSet.split("_");

            fsWeightLevel = split[0];
            weightPickIndex = split[1];
            
        }

        let weightPick = 0;
        let weightArrayCheck = isFs ? weights.f : weights;

        for (let j = 1; j <= Number(weightPickIndex); j++) {
            if (isFs) {
                weightPick += weights[fsWeightLevel][j - 1];
            } else {
                weightPick += weights[j - 1];
            }
        }

        /// Don't add reel weightpick if there is only one reel to pick from
        const weightString = weightArrayCheck?.length > 1 ? `${weightPick},` : ""

        setOutput(`
            ${rejectionSet ? `${maths[rtp].reductionWeight1M + 1},` : ""}${weightString}${reelStops.join(",")}`);
    }, [dataRtp, selectedReelSet, rejectionSet, reelStops]);

    const arrowClicked = (reelIndex, adjustment) => {
        const stored = [...reelStops];
        const chosenReelStrip = reelSets[selectedReelSet][reelIndex];

        let proposedStop = (stored[reelIndex] + adjustment) % chosenReelStrip.length;
        if (proposedStop < 0) {
            proposedStop = chosenReelStrip.length - 1;
        }

        stored[reelIndex] = proposedStop;

        setReelStops(stored);
    };

    return (
        <div className="force-container flex">
            <div className="settings-container flex">
                <div className="select-container container-stacker">
                    <p className="fixed-size-p">RTP Variant</p>
                    <select className="force-input-output" value={rtp} onChange={(e) => setRtp(e.target.value)}>
                        {rtps.map((key) => (
                            <option key={key} value={key}>
                                {key}%
                            </option>
                        ))}
                    </select>
                </div>
                <div className="select-container container-stacker">
                    <p className="fixed-size-p">Reel Sets</p>
                    <select
                        className="force-input-output"
                        value={selectedReelSet}
                        onChange={(e) => setSelectedReelSet(e.target.value)}
                    >
                        {Object.keys(reelSets).map((key) => (
                            <option key={key} value={key}>
                                {key[0] != "f"
                                    ? `Base Game Reel Set ${key}`
                                    : `Free Spins Reel Set ${key.slice(1).replace("_", /\d$/.test(key) ? " " : "")}`}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="checkbox-container">
                    <p>Show Symbol IDs</p>
                    <input
                        type="checkbox"
                        style={{ width: "auto" }}
                        checked={showIds}
                        onChange={(e) => setShowIds(e.target.checked)}
                    />
                </div>
                <div className="select-container" style={{ marginTop: "20px" }}>
                    <div className="force-input-output">{output}</div>
                    <div
                        onClick={() => {
                            navigator.clipboard.writeText(output.trim()).then(() => {
                                setCopiedStyle({ margin: 0, opacity: 1 });
                                setTimeout(() => {
                                    setCopiedStyle({ margin: 0, transition: "opacity 1s ease-in", opacity: 0 });
                                }, 100);
                            });
                        }}
                        className="copy-btn clickable"
                        style={{ backgroundImage: `url(${copyBtn})` }}
                    ></div>
                </div>
                <p style={copiedStyle}>Force Copied!</p>
            </div>
            <div className="reels">
                {displayData.map((reel, index) => (
                    <div key={`reel${index}`} className="reel">
                        <div
                            className="arrow clickable"
                            onClick={() => {
                                arrowClicked(index, 1);
                            }}
                            style={{ backgroundImage: `url(${arrow})` }}
                        ></div>
                        {reel.map((symId, symIndex) => {
                            const invertedSymId = Math.abs(symId - config.highestSymId);

                            return (
                                <div
                                    key={`sym${symIndex}`}
                                    className="reel-block"
                                    style={{
                                        opacity: symIndex == 0 || symIndex == reel.length - 1 ? 0.3 : 1,
                                    }}
                                >
                                    <div
                                        className="reel-block-img"
                                        style={{ backgroundImage: `url(${config.symbolImgs[invertedSymId]})` }}
                                    ></div>
                                    {showIds && <div className="symId">{invertedSymId}</div>}
                                </div>
                            );
                        })}
                        <div
                            className="arrow reverse clickable"
                            onClick={() => {
                                arrowClicked(index, -1);
                            }}
                            style={{ backgroundImage: `url(${arrow})` }}
                        ></div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ForceModule;
